import { apiFetch } from "./client";

export type JobType = "instagram_signup";
export type ScriptFormat = "jsonc";

export interface ScriptSummary {
  id: string;
  name: string;
  description: string | null;
  job_type: JobType;
  content_format: ScriptFormat;
  is_active: boolean;
  content_length: number;
  created_at: string;
  updated_at: string;
}

export interface Script extends ScriptSummary {
  content: string;
}

// Input déclaré à la racine d'un script (clé `inputs`). Chaque input génère un
// champ dans la modale de lancement (popup "ask_user") ; la valeur saisie est
// envoyée dans job.params et interpolée via ${var} dans le script.
export interface ScriptInput {
  var: string;
  prompt: string;
  type?: "text" | "number";
  default?: string | number;
  placeholder?: string;
}

// Retire les commentaires JSONC (// et /* */) en respectant les chaînes, puis
// supprime les virgules traînantes. Best-effort : ne gère que les chaînes
// double-quote (JSON standard).
function stripJsonc(src: string): string {
  let out = "";
  let inString = false;
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    const next = src[i + 1];
    if (inString) {
      out += c;
      if (c === "\\") {
        out += next ?? "";
        i += 2;
        continue;
      }
      if (c === '"') inString = false;
      i++;
      continue;
    }
    if (c === '"') {
      inString = true;
      out += c;
      i++;
      continue;
    }
    if (c === "/" && next === "/") {
      while (i < src.length && src[i] !== "\n") i++;
      continue;
    }
    if (c === "/" && next === "*") {
      i += 2;
      while (i < src.length && !(src[i] === "*" && src[i + 1] === "/")) i++;
      i += 2;
      continue;
    }
    out += c;
    i++;
  }
  // Virgules traînantes avant } ou ]
  return out.replace(/,(\s*[}\]])/g, "$1");
}

// Parse le contenu d'un script et renvoie ses `inputs` (ou [] si absent/invalide).
export function parseScriptInputs(content: string): ScriptInput[] {
  try {
    const obj = JSON.parse(stripJsonc(content)) as { inputs?: unknown };
    if (!Array.isArray(obj.inputs)) return [];
    return (obj.inputs as ScriptInput[]).filter(
      (i) => i && typeof i.var === "string" && typeof i.prompt === "string"
    );
  } catch {
    return [];
  }
}

interface ListResponse { success: boolean; scripts: ScriptSummary[]; }
interface SingleResponse { success: boolean; script: Script; }

export async function listScripts(): Promise<ScriptSummary[]> {
  const data = await apiFetch<ListResponse>("/api/scripts");
  return data.scripts;
}

export async function getScript(id: string): Promise<Script> {
  const data = await apiFetch<SingleResponse>(`/api/scripts/${id}`);
  return data.script;
}

export async function createScript(payload: {
  name: string;
  description?: string;
  job_type?: JobType;
  content: string;
}): Promise<Script> {
  const data = await apiFetch<SingleResponse>("/api/scripts", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.script;
}

export async function updateScript(
  id: string,
  payload: Partial<Pick<Script, "name" | "description" | "job_type" | "content" | "is_active">>
): Promise<Script> {
  const data = await apiFetch<SingleResponse>(`/api/scripts/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return data.script;
}

export async function deleteScript(id: string): Promise<void> {
  await apiFetch(`/api/scripts/${id}`, { method: "DELETE" });
}

export function buildScriptExportUrl(id: string): string {
  return `/api/scripts/${id}/export`;
}

// Récupère le contenu brut d'un script exporté pour download navigateur.
export async function downloadScriptExport(id: string, fallbackName: string): Promise<void> {
  const token = localStorage.getItem("velvet_hub.token");
  const res = await fetch(buildScriptExportUrl(id), {
    headers: { Authorization: `Bearer ${token ?? ""}` },
  });
  if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `velvet-script-${fallbackName}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
